import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Clock, ChevronRight, BookOpen, Brain, Moon, Shield, Zap, Sparkles, Heart } from 'lucide-react';

const BLOG_POSTS = [
  {
    id: 1,
    slug: 'stres-nasil-yonetilir',
    title: 'Stres Nasıl Yönetilir? Bilimsel Yöntemlerle Günlük Stres Kontrolü',
    description: 'Kronik stres vücudunuzu ve zihninizi olumsuz etkiler. Günlük uygulanabilir bilimsel yöntemlerle stresi kontrol altına almak mümkündür.',
    category: 'stres',
    readTime: 6,
  },
  {
    id: 2,
    slug: 'uyku-kalitesi-nasil-artirilir',
    title: 'Uyku Kalitesi Nasıl Artırılır? 8 Bilimsel Öneri',
    description: 'İyi uyku, fiziksel ve zihinsel sağlığın temelidir. Uyku kalitenizi artırmak için uygulanabilir 8 bilimsel öneri.',
    category: 'uyku',
    readTime: 5,
  },
  {
    id: 3,
    slug: 'dikkat-eksikligi-belirtileri',
    title: 'Dikkat Eksikliği Belirtileri Nelerdir?',
    description: 'Dikkat eksikliği yalnızca çocukluk döneminde değil, yetişkinlikte de görülür. Erken fark edilmesi yaşam kalitesini önemli ölçüde artırabilir.',
    category: 'dikkat',
    readTime: 7,
  },
  {
    id: 4,
    slug: 'kaygi-ile-basa-cikma',
    title: 'Kaygı ile Başa Çıkmanın 5 Etkili Yolu',
    description: 'Kaygı, doğal bir tepki olmakla birlikte günlük yaşamı etkileyen bir sorun haline gelebilir. İşte bilimsel olarak kanıtlanmış 5 yöntem.',
    category: 'kaygi',
    readTime: 5,
  },
  {
    id: 5,
    slug: 'hafiza-guclendirme-teknikleri',
    title: 'Hafızanızı Güçlendirmek İçin Günlük Alışkanlıklar',
    description: 'Beyin plastisitesi sayesinde hafızanızı her yaşta güçlendirebilirsiniz. Basit günlük alışkanlıklarla bilişsel performansınızı artırın.',
    category: 'hafiza',
    readTime: 6,
  },
  {
    id: 6,
    slug: 'dijital-detoks-neden-onemli',
    title: 'Dijital Detoks Neden Önemli? Ekran Süresi ve Zihinsel Sağlık',
    description: 'Aşırı ekran süresi dikkat, uyku ve ruh halinizi doğrudan etkiler. Dijital detoks yöntemleriyle dengenizi yeniden kurun.',
    category: 'genel',
    readTime: 4,
  },
];

const CATEGORY_META = {
  uyku: { label: 'Uyku', icon: Moon, color: '#D9A258' },
  dikkat: { label: 'Dikkat', icon: Brain, color: '#6F90B1' },
  kaygi: { label: 'Kaygı', icon: Shield, color: '#D88A61' },
  stres: { label: 'Stres', icon: Zap, color: '#C25D4F' },
  hafiza: { label: 'Hafıza', icon: Sparkles, color: '#5A9E7A' },
  genel: { label: 'Genel', icon: Heart, color: '#8E9BAA' },
};

const ALL_CATEGORIES = ['stres', 'uyku', 'dikkat', 'kaygi', 'hafiza', 'genel'];

const BlogListPage = () => {
  const [activeCategory, setActiveCategory] = useState('');

  const filteredPosts = activeCategory
    ? BLOG_POSTS.filter(p => p.category === activeCategory)
    : BLOG_POSTS;

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #15263A 0%, #1E3348 80px, #F8F6F3 80px, #F8F6F3 100%)' }}>
      <Helmet>
        <title>Blog — DoktorCebinde</title>
        <meta name="description" content="Uyku, dikkat, kaygı, stres ve enerji hakkında bilimsel ama anlaşılır yazılar." />
      </Helmet>
      <Header />

      {/* Hero */}
      <section style={{ paddingTop: 48, paddingBottom: 40 }}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.14em', color: '#94A3B8', marginBottom: 12 }}>DoktorCebinde Blog</p>
          <h1 style={{ fontSize: 32, fontWeight: 700, color: '#1A2B3C', lineHeight: 1.2, marginBottom: 12 }} data-testid="blog-page-title">
            Zihin Sağlığını Anlamak
          </h1>
          <p style={{ color: '#687482', fontSize: 15, maxWidth: 460, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.6 }}>
            Uyku, dikkat, kaygı, stres ve enerji hakkında bilimsel ama anlaşılır yazılar.
          </p>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-4xl mx-auto px-4 mb-10">
        <div className="flex flex-wrap gap-2 justify-center" data-testid="blog-categories">
          <button
            onClick={() => setActiveCategory('')}
            className="text-sm font-medium transition-all"
            style={{
              padding: '8px 16px',
              borderRadius: 4,
              background: !activeCategory ? '#1A2B3C' : 'transparent',
              color: !activeCategory ? '#fff' : '#687482',
              border: !activeCategory ? '1px solid #1A2B3C' : '1px solid rgba(0,0,0,0.10)',
            }}
            data-testid="category-all"
          >
            Tümü ({BLOG_POSTS.length})
          </button>
          {ALL_CATEGORIES.map(catKey => {
            const meta = CATEGORY_META[catKey];
            const Icon = meta.icon;
            const isActive = activeCategory === catKey;
            const count = BLOG_POSTS.filter(p => p.category === catKey).length;
            return (
              <button
                key={catKey}
                onClick={() => setActiveCategory(isActive ? '' : catKey)}
                className="flex items-center gap-1.5 text-sm font-medium transition-all"
                style={{
                  padding: '8px 14px',
                  borderRadius: 4,
                  background: isActive ? '#1A2B3C' : 'transparent',
                  color: isActive ? '#fff' : '#687482',
                  border: isActive ? '1px solid #1A2B3C' : '1px solid rgba(0,0,0,0.10)',
                }}
                data-testid={`category-${catKey}`}
              >
                <Icon className="w-3.5 h-3.5" /> {meta.label} ({count})
              </button>
            );
          })}
        </div>
      </section>

      {/* Posts Grid */}
      <section className="max-w-4xl mx-auto px-4 pb-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="blog-posts-grid">
          {filteredPosts.map(post => {
            const catMeta = CATEGORY_META[post.category] || CATEGORY_META.genel;
            const CatIcon = catMeta.icon;
            return (
              <div
                key={post.id}
                className="group overflow-hidden transition-transform hover:scale-[1.015]"
                style={{
                  background: '#fff',
                  border: '1px solid #ECE7E1',
                  borderRadius: 14,
                }}
                data-testid={`blog-post-card-${post.id}`}
              >
                {/* Color accent top */}
                <div style={{ height: 3, background: catMeta.color }} />

                {/* Icon area */}
                <div className="flex items-center justify-center" style={{ height: 120, background: '#FAF8F5' }}>
                  <CatIcon style={{ width: 36, height: 36, color: catMeta.color, opacity: 0.3 }} />
                </div>

                <div style={{ padding: '20px 22px 22px' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="flex items-center gap-1 text-xs font-semibold" style={{ color: catMeta.color }}>
                      <CatIcon style={{ width: 12, height: 12 }} /> {catMeta.label}
                    </span>
                    <span className="flex items-center gap-1 text-xs" style={{ color: '#94A3B8' }}>
                      <Clock style={{ width: 11, height: 11 }} /> {post.readTime} dk
                    </span>
                  </div>

                  <h2 className="line-clamp-2" style={{ fontSize: 15, fontWeight: 700, color: '#1A2B3C', lineHeight: 1.4, marginBottom: 8 }}>
                    {post.title}
                  </h2>

                  <p className="line-clamp-2" style={{ fontSize: 13, color: '#687482', lineHeight: 1.55, marginBottom: 16 }}>
                    {post.description}
                  </p>

                  <div className="flex items-center justify-end">
                    <span className="flex items-center gap-1 text-xs font-medium" style={{ color: '#94A3B8' }}>
                      Yakında <ChevronRight style={{ width: 12, height: 12 }} />
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-12">
          <p style={{ color: '#94A3B8', fontSize: 14 }}>
            Yeni yazılar hazırlanmaktadır. Düzenli olarak yeni içerikler eklenmektedir.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogListPage;
